/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useState, useRef, useEffect } from 'react';
import Fuse from 'fuse.js';

import Autosuggest from 'react-autosuggest';
import { CheckboxInputGroupAccessibleWDP, Button, Accordion, Alert } from '@components';
import { iSponsor, iSuburb } from '@interfaces/interfaces';
import Checkbox from '@components/CheckboxInput/Checkbox';
import theme from '@components/SearchBox/Autosuggest.theme.module.scss';
import styles from '@components/SearchBox/SearchBox.module.scss';

import SearchResults from '@components/SearchBox/SearchResults';

import surroundingSuburbsData from '../../data/surrounding_suburb_file_vic.json';

import { ReactComponent as DropdownIcon } from '../../images/dropdown.svg';
import { ReactComponent as SearchIcon } from '../../images/search.svg';

interface iSponsorInfoData {
  data: iSponsor[];
}

const NO_RESULTS_ERROR_MESSAGE = 'There are no sponsors in the suburb you searched. Clear your search and try searching a different suburb or tick ‘Show only Telehealth sponsors’.';

const isSuburbVictorian = (postcode: string) => postcode.startsWith('3');

const SearchBySuburb = ({ data }: iSponsorInfoData): any => {
  const searchResultRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLInputElement>(null);
  const activityDropdownRef = useRef<HTMLInputElement>(null);
  const serviceDropdownRef = useRef<HTMLInputElement>(null);

  // Append to the surrounding suburbs data if a sponsor exists outside of Vic
  data.forEach((sponsor) => {
    if (!surroundingSuburbsData.some((a) => (a.suburb_name.trim() === sponsor.Suburb.trim()
      && (isSuburbVictorian(sponsor.Postcode) || a.postcode === sponsor.Postcode)))) {
      surroundingSuburbsData.push({
        suburb_name: sponsor.Suburb,
        postcode: sponsor.Postcode,
        state: '',
        surrounding_suburbs: [],
        surrounding_postcodes: [],
        longitude: 0,
        latitude: 0
      });
    }
  });
  const surroundingSuburbDict: Record<string, string[]> = surroundingSuburbsData.reduce((acc, suburb) => {
    acc[suburb.suburb_name] = suburb.surrounding_suburbs;
    return acc;
  }, {} as Record<string, string[]>);

  const fuseOptions = {
    keys: ['suburb_name', 'postcode'],
    threshold: 0.5,
  };
  const fuse = new Fuse(surroundingSuburbsData, fuseOptions);

  const exactSuburbsOptions = {
    keys: ['suburb_name', 'postcode'],
    threshold: 0,
  };
  const exactSuburbFuse = new Fuse(surroundingSuburbsData, exactSuburbsOptions);

  const fuseSponsorsOptions = {
    keys: ['Suburb'],
    threshold: 0,
  };
  const fuseSponsors = new Fuse(data, fuseSponsorsOptions);

  // The value that user types/selects from the dropdown, displayed in the Input box as the placeholder.
  const [searchTextToDisplay, setSearchTextToDisplay] = useState('');
  // The value that the user has searched for. Used primarily in search logic
  const [searchText, setSearchText] = useState('');
  // The searched Text that displays in the results. Typically in format `Suburb, Postcode` if selected from autoselect.
  // Otherwise, the same as searchText.
  const [searchedTextDisplayStr, setSearchedTextDisplayStr] = useState('');

  // The state handling the showSurrounding Suburbs checkbox.
  const [showSurroundingSuburbs, setShowSurroundingSuburbs] = useState(true);

  const [searchedData, setSearchedData] = useState<iSponsor[]>([]);
  const [filteredData, setFilteredData] = useState<iSponsor[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [showActivitiesDropdown, setShowActivitiesDropdown] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showSearchResult, setshowSearchResult] = useState(false);
  const [showFilterResult, setShowFilterResult] = useState(false);
  const [telehealthOnly, setTelehealthOnly] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<iSuburb[]>([]);

  const [suburbResultsCount, setSuburbResultCount] = useState<null | number>(0);
  const [telehealthResultsCount, setTelehealthResultsCount] = useState<null | number>(0);

  const [postcodeSearched, setPostcodeSearched] = useState('');

  const [clearButtonClicked, setClearButtonClicked] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const loadingTime = 500;

  const telehealthSponsors = data.filter((item) => item.Telehealth.toLocaleLowerCase() === 'yes');

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }
    // Initial check on mount
    handleResize();

    // Listen for window resize events
    window.addEventListener('resize', handleResize);
    const handleClickOutside = (e: any) => {
      if (activityDropdownRef.current && !activityDropdownRef.current.contains(e.target) && serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target)) {
        setShowActivitiesDropdown(false);
        setShowServicesDropdown(false);
      }
    };
    const handleEscKey = (e: any) => {
      if (e.key === 'Escape') {
        setShowActivitiesDropdown(false);
        setShowServicesDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  const applyFilters = (dataToFilter = searchedData, timeToLoad: number) => {
    setLoading(true);
    setTimeout(() => {
      let filtered = dataToFilter;
      if (selectedActivities.length > 0) {
        filtered = filtered.filter((item) => item.Activities.some((act) => selectedActivities.includes(act)));
      }
      if (selectedServices.length > 0) {
        filtered = filtered.filter((item) => item.Services.some((act) => selectedServices.includes(act)));
      }

      if (telehealthOnly) {
        filtered = filtered.filter((item) => item.Telehealth.toLocaleLowerCase() === 'yes');
      }
      // Create a Set of OrgName from the filtered results
      const telehealthResultsOrgNames = new Set(telehealthSponsors.map((item) => item.OrgName));

      const seenOrgNames = new Set();
      // Count how many items in filtered results are also telehealth. However do not include duplicates
      const telehealthCount = filtered.reduce((count, item) => {
        if (telehealthResultsOrgNames.has(item.OrgName) && !seenOrgNames.has(item.OrgName)) {
        seenOrgNames.add(item.OrgName);
            return count + 1;
        }
        return count;
      }, 0);

      if (searchTextToDisplay.trim()) {
        setSuburbResultCount(filtered.length - telehealthCount);
        setTelehealthResultsCount(telehealthCount);
      }

      setFilteredData(filtered);
      setShowFilterResult(true);
      setshowSearchResult(false);
      setLoading(false);
      // setShowActivitiesDropdown(false);
      // setShowServicesDropdown(false);
    }, timeToLoad);
  };

  const handleSearchBySuburb = (searchInput: string, postcode = ''): iSponsor[] => {
    let results = [];

    const exactSuburbList = exactSuburbFuse.search(searchInput).map((result) => result.item);

    results = fuseSponsors.search(searchInput).map((result) => result.item);
    // If there are more than 1 suburb with the same name, consider the postcode in the search
    if (exactSuburbList.length > 1 && postcode) {
      results = results.filter((item) => {
        if (isSuburbVictorian(postcode) && isSuburbVictorian(item.Postcode)) {
        return true;
        }
        return item.Postcode === postcode;
    });
    }

    if (showSurroundingSuburbs && (!(exactSuburbList.length > 1) || isSuburbVictorian(postcode))) {
      const surroundingSuburbsList = surroundingSuburbDict[searchInput];
      for (let index = 0; index < surroundingSuburbsList?.length; index += 1) {
        results.push(...fuseSponsors.search(surroundingSuburbsList[index]).map((result) => result.item));
      }
    }
    setSuburbResultCount(results.length);
    setTelehealthResultsCount(telehealthSponsors.length);

    results.push(...telehealthSponsors);
    return results;
  };

  const handleSearch = (displayText: string, searchInput: string, postcode = '') => {
    setError('');
    setLoading(true);
    setSuburbResultCount(0);
    setTelehealthResultsCount(0);
    setPostcodeSearched(postcode);
    setTimeout(() => {
      setSearchTextToDisplay(displayText);
      setSearchText(searchInput);
      setSearchedTextDisplayStr(displayText);

      let results = [];
      setShowFilterResult(false);
      setshowSearchResult(true);
      if (!searchInput.trim()) {
        results = data.sort((a, b) => a.OrgName.trim().localeCompare(b.OrgName.trim()));
        setSearchedData(results);
        setSuburbResultCount(null);
        setTelehealthResultsCount(null);
      } else {
        results = handleSearchBySuburb(searchInput, postcode);
        setSearchedData(results);
      }
      if (selectedActivities.length > 0 || selectedServices.length > 0 || telehealthOnly) {
        applyFilters(results, 0);
      }
      focusRef.current?.blur();
      if (isMobile) {
        setTimeout(() => {
          searchResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
      setLoading(false);
    }, loadingTime);
  };

  useEffect(() => {
    if (searchedData.length > 0 && !clearButtonClicked) applyFilters(searchedData, loadingTime);
  }, [selectedActivities, selectedServices, telehealthOnly]);

  useEffect(() => {
    if (searchedData.length > 0 || searchedTextDisplayStr) {
      setLoading(true);
      setTimeout(() => {
        let results = [];
        setShowFilterResult(false);
        setshowSearchResult(true);
        if (!searchText.trim()) {
          results = data.sort((a, b) => a.OrgName.trim().localeCompare(b.OrgName.trim()));
          setSearchedData(results);
          setSuburbResultCount(null);
          setTelehealthResultsCount(null);
        } else {
          results = handleSearchBySuburb(searchText, postcodeSearched);
          setSearchedData(results);
        }
        if (selectedActivities.length > 0 || selectedServices.length > 0 || telehealthOnly) {
          applyFilters(results, 0);
        }
        focusRef.current?.blur();
        if (isMobile) {
          setTimeout(() => {
            searchResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        }
        setLoading(false);
      }, loadingTime);
    }
  }, [showSurroundingSuburbs]);

  const allActivities = Array.from(new Set(data.flatMap((item) => item.Activities)));
  const allServices = Array.from(new Set(data.flatMap((item) => item.Services)));

  const getSuggestions = (inpuValue: string) => {
    if (!inpuValue) setSuggestions([]);
    const suggest = fuse
      .search(inpuValue)
      .map((result) => result.item)
      .slice(0, 5);
    setSuggestions(suggest);
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    getSuggestions(value);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestionValue = (suggestion: iSuburb): string => {
    const suggestionValue = `${suggestion.suburb_name}, ${suggestion.postcode}`;
    if (suggestionValue) return suggestionValue;
    return '';
  };

  const renderSuggestion = (suggestion: iSuburb): ReactElement => {
    const suggestionString = suggestion.suburb_name;
    if (suggestionString) {
      return (
        <div>
          {suggestion.suburb_name}
          ,
          {' '}
          {suggestion.postcode}
        </div>
      );
    }
    return <></>;
  };

  // update search query = suggestion
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSuggestionSelected = async (event: any, { suggestion }: { suggestion: iSuburb }) => {
    const suburbWithPostcode = `${suggestion.suburb_name}, ${suggestion.postcode}`;
    const suburbName = suggestion.suburb_name;
    const postCode = suggestion.postcode;
    if (suburbWithPostcode && suburbName) {
      handleSearch(suburbWithPostcode, suburbName, postCode);
    }
  };

  const handleSearchByString = () => {
    const suggest = fuse
      .search(searchTextToDisplay)
      .map((result) => result.item)
      .slice(0, 1);
    if (Number(searchTextToDisplay)) {
      if (suggest[0]?.postcode === searchTextToDisplay) {
        handleSearch(searchTextToDisplay, suggest[0]?.suburb_name, suggest[0]?.postcode);
        return;
      }
    } else if (suggest[0]?.suburb_name.toLocaleLowerCase() === searchTextToDisplay.toLocaleLowerCase()) {
      handleSearch(searchTextToDisplay, suggest[0]?.suburb_name);
      return;
    }
    handleSearch(searchTextToDisplay, searchTextToDisplay);
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      if (searchTextToDisplay === '') {
        handleSearch(searchTextToDisplay, searchTextToDisplay);
        return;
      }
      handleSearchByString();
    }
  };

  const handleSearchButtonClick = () => {
    if (searchTextToDisplay === searchedTextDisplayStr) {
      handleSearch(searchTextToDisplay, searchText);
      return;
    }

    if (searchTextToDisplay === '') {
      handleSearch(searchTextToDisplay, '');
      return;
    }
    handleSearchByString();
  };

  const inputProps = {
    placeholder: 'Search for a suburb',
    value: searchTextToDisplay,
    onChange: (event: any, { newValue }: { newValue: string }) => {
      setSearchTextToDisplay(newValue);
      // setSearchText(newValue);
    },
    onKeyDown: handleKeyDown,
    ref: focusRef,
  };

  const clearButtonClick = () => {
    setClearButtonClicked(true);
    if (selectedActivities.length > 0 || telehealthOnly || selectedServices.length > 0) {
      setSelectedActivities([]);
      setSelectedServices([]);
      setSearchTextToDisplay('');
      setTelehealthOnly(false);
      setShowActivitiesDropdown(false);
      setShowServicesDropdown(false);
    }
    if (searchTextToDisplay) {
      setSearchTextToDisplay('');
    }
    if (focusRef.current) {
      focusRef.current.setAttribute('tabindex', '-1');
      focusRef.current.focus();
    }
    setTimeout(() => setClearButtonClicked(false), 0);
  };

  return (
    <div>
      <div className={styles.searchContainer}>
        <div className={styles.searchTitle}>Search by suburb</div>
        <div className={styles.searchBoxContainer}>
          <div id="suburb-search-input" className={styles.autosuggestContainer} tabIndex={-1}>
            <div className={styles.autosuggest}>
              <SearchIcon className={styles.searchIcon} />
              <Autosuggest
                suggestions={suggestions}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                onSuggestionSelected={onSuggestionSelected}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={renderSuggestion}
                inputProps={inputProps}
                theme={theme}
              />
            </div>
          </div>
          <div>
            <Checkbox title="Show surrounding suburbs" state={showSurroundingSuburbs} setState={setShowSurroundingSuburbs} />
          </div>
          <div id="suburb-search-button" className={styles.searchButtonContainer}>
            <Button size="small" onClick={() => handleSearchButtonClick()}>
              Search
            </Button>
          </div>
        </div>
        {/* {error && <Alert icon={IssueIcon} color="red" message={error} theme="error" />} */}
        {isMobile ? (
          <div className={styles.accordionContainer}>
            <Accordion
              title="Filter search results"
              variant="wdp"
              popupContent={(
                <div className={styles.filterBoxContainer}>
                  <div className={styles.filtersContainer}>
                    <div className={styles.filterContainer}>
                      <div className={styles.searchTitle3}>Type of activities they offer:</div>
                      <div ref={activityDropdownRef} id="suburb-activities-dropdown" className={styles.selectContainer}>
                        <span
                          className={styles.checkboxSelect}
                          id="activity"
                          title="Activity"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              setShowActivitiesDropdown(!showActivitiesDropdown);
                            }
                          }}
                          onClick={() => setShowActivitiesDropdown(!showActivitiesDropdown)}
                          aria-label="Select"
                          role="button">
                          {selectedActivities.length === 1 && `${selectedActivities.length} filter selected`}
                          {selectedActivities.length > 1 && `${selectedActivities.length} filters selected`}
                        </span>
                        <DropdownIcon className={styles.arrowIcon} />

                        {showActivitiesDropdown && (
                          <div className={styles.dropdownContainer}>
                            <CheckboxInputGroupAccessibleWDP
                              title="Activities"
                              options={allActivities}
                              selectedValues={selectedActivities}
                              onSelectionChange={setSelectedActivities}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.filterContainer}>
                      <div className={styles.searchTitle3}>Services for:</div>
                      <div ref={serviceDropdownRef} id="suburb-services-dropdown" className={styles.selectContainer}>
                        <span
                          className={styles.checkboxSelect}
                          id="services"
                          title="Services"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              setShowServicesDropdown(!showServicesDropdown);
                            }
                          }}
                          onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                          aria-label="Select"
                          role="button">
                          {selectedServices.length === 1 && `${selectedServices.length} filter selected`}
                          {selectedServices.length > 1 && `${selectedServices.length} filters selected`}
                        </span>
                        <DropdownIcon className={styles.arrowIcon} />

                        {showServicesDropdown && (
                          <div className={styles.dropdownContainer2}>
                            <CheckboxInputGroupAccessibleWDP title="Services" options={allServices} selectedValues={selectedServices} onSelectionChange={setSelectedServices} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.filterButtons}>
                    <div id="suburb-telehealth-checkbox">
                      <Checkbox title="Show only Telehealth sponsors" state={telehealthOnly} setState={setTelehealthOnly} />
                    </div>
                    <div className={styles.filterButtonContainer}>
                      <Button size="small" onClick={clearButtonClick} color="transparent">
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        ) : (
          <div className={styles.filterBoxContainer}>
            <div className={styles.searchTitle2}>Filter search results</div>
            <div className={styles.filtersContainer}>
              <div className={styles.filterContainer}>
                <div className={styles.searchTitle3}>Type of activities they offer:</div>
                <div ref={activityDropdownRef} id="suburb-activities-dropdown" className={styles.selectContainer}>
                  <span
                    className={styles.checkboxSelect}
                    id="activity"
                    title="Activity"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        setShowActivitiesDropdown(!showActivitiesDropdown);
                      }
                    }}
                    onClick={() => setShowActivitiesDropdown(!showActivitiesDropdown)}
                    aria-label="Select"
                    role="button">
                    {selectedActivities.length === 1 && `${selectedActivities.length} filter selected`}
                    {selectedActivities.length > 1 && `${selectedActivities.length} filters selected`}
                  </span>
                  <DropdownIcon className={styles.arrowIcon} />

                  {showActivitiesDropdown && (
                    <div className={styles.dropdownContainer}>
                      <CheckboxInputGroupAccessibleWDP title="Activities" options={allActivities} selectedValues={selectedActivities} onSelectionChange={setSelectedActivities} />
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.filterContainer}>
                <div className={styles.searchTitle3}>Services for:</div>
                <div ref={serviceDropdownRef} id="suburb-services-dropdown" className={styles.selectContainer}>
                  <span
                    className={styles.checkboxSelect}
                    id="services"
                    title="Services"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        setShowServicesDropdown(!showServicesDropdown);
                      }
                    }}
                    onClick={() => setShowServicesDropdown(!showServicesDropdown)}
                    aria-label="Select"
                    role="button">
                    {selectedServices.length === 1 && `${selectedServices.length} filter selected`}
                    {selectedServices.length > 1 && `${selectedServices.length} filters selected`}
                  </span>
                  <DropdownIcon className={styles.arrowIcon} />

                  {showServicesDropdown && (
                    <div className={styles.dropdownContainer2}>
                      <CheckboxInputGroupAccessibleWDP title="Services" options={allServices} selectedValues={selectedServices} onSelectionChange={setSelectedServices} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.filterButtons}>
              <div id="suburb-telehealth-checkbox">
                <Checkbox title="Show only Telehealth sponsors" state={telehealthOnly} setState={setTelehealthOnly} />
              </div>
              <div className={styles.filterButtonContainer}>
                <Button size="small" onClick={clearButtonClick} color="transparent">
                  Clear
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div>
        {loading ? (
          <div className={styles.loading}>
            <div className={`${styles.cssloadcontainer} ${styles.inline}`}>
              <div className={styles.cssloadwhirlpool} />
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '20px' }} ref={searchResultRef}>
            {showFilterResult && !error && <SearchResults results={filteredData} searchInput={searchedTextDisplayStr} noResultsError={NO_RESULTS_ERROR_MESSAGE} sponsorResultCount={suburbResultsCount} telehealthResultsCount={telehealthResultsCount} />}
            {showSearchResult && !error && <SearchResults results={searchedData} searchInput={searchedTextDisplayStr} noResultsError={NO_RESULTS_ERROR_MESSAGE} sponsorResultCount={suburbResultsCount} telehealthResultsCount={telehealthResultsCount} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBySuburb;
